/**
 * Personas: el perfil de cada persona que le escribe a un cliente (lib/user-profiles en la API).
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import type { StatsMessage } from "@/hooks/use-moderation-stats";

export type UserState =
  | "spam"
  | "conflictive"
  | "open_claim"
  | "vip"
  | "influencer"
  | "fan"
  | "recurrent"
  | "new"
  | "occasional";

export interface PersonProfile {
  userId: string;
  username: string | null;
  conversationId: string | null;
  platforms: string[];
  firstSeen: string;
  lastSeen: string;
  messages: number;
  comments: number;
  dms: number;
  activeDays: number;
  outcomes: Record<"answeredByBot" | "answeredByHuman" | "waiting" | "notNeeded" | "pending", number>;
  reasons: Record<string, number>;
  complaints: number;
  mentions: number;
  tags: string[];
  notes: string | null;
  state: UserState;
  flags: UserState[];
  score: number;
  lastMessage: { body: string; attachment: string | null; at: string };
}

export interface PeopleData {
  days: number;
  summary: { people: number; recurrent: number; byState: Record<string, number>; averageScore: number | null };
  people: PersonProfile[];
}

export function usePeople(clientSlug: string | undefined, days: number) {
  return useQuery<PeopleData>({
    queryKey: ["people", clientSlug, days],
    enabled: !!clientSlug,
    queryFn: () => apiGet<PeopleData>(`/people?clientSlug=${encodeURIComponent(clientSlug!)}&days=${days}`),
  });
}

export function usePerson(clientSlug: string | undefined, userId: string | null, days: number) {
  return useQuery<{ profile: PersonProfile; messages: StatsMessage[] }>({
    queryKey: ["person", clientSlug, userId, days],
    enabled: !!clientSlug && !!userId,
    queryFn: () =>
      apiGet(`/people/${encodeURIComponent(clientSlug!)}/${encodeURIComponent(userId!)}?days=${days}`),
  });
}
